/* ============================================================================
   bulk-grid.js — the hero product wall on /pages/bulk
   ----------------------------------------------------------------------------
   WHAT IT IS
   A 4-across grid showing five rows of product at a time. Scroll inside the
   panel to browse the whole range; the page behind it stays put. Chips above it
   switch collections. Tapping a product adds it to the enquiry — it is never a
   link, because this is a lead page and every link is a way to leave it.

   ─── WHY THERE IS NO VIRTUAL SCROLLING HERE (READ BEFORE "OPTIMISING") ─────
   An earlier version recycled a fixed window of ~36 tiles: reach the bottom and
   the top row was moved to the end, re-pointed at new products, and the scroll
   position nudged by one row to hide the seam. It was clever, it worked, and it
   FELT BAD.

   That is not fixable by tuning. Assigning `scrollTop` from JavaScript
   interrupts the browser's momentum scrolling — the native flick animation is
   cancelled the instant you touch the scroll position. On a phone it reads as a
   sticky, stuttering list however well the recycling itself performs.

   So the wall now just renders the products and lets the browser scroll them.
   There is NO scroll handler at all, and that is exactly why it is smooth: the
   scrolling is entirely native, in both directions, with real momentum.

   The DOM worry that motivated recycling doesn't apply at this size. Sixty
   images with no forms, no price logic and no per-card scripts is nothing like
   the hundreds of full product cards that wrecked this store's homepage in
   July. Keep the cap and this stays cheap.
   ========================================================================== */
(function () {
  'use strict';

  /* No cap on how many products a range can show — the wall is meant to be the
     whole collection. Shopify's products.json returns up to 250 per request and
     we page through until it's exhausted. Tiles are plain images with no forms
     or per-card scripts, so even the largest range here (211 building blocks)
     is cheap; everything below the first five rows is lazy-loaded. */
  var PER_PAGE = 250;

  function init(root) {
    if (!root || root.__gridInit) return;
    root.__gridInit = true;

    var wall = root.querySelector('[data-wall]');
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-crate-chip]'));
    var nameEl = root.querySelector('[data-crate-name]');
    if (!wall) return;

    var sets = [];
    try { sets = JSON.parse(root.querySelector('[data-crate-data]').textContent || '[]'); } catch (e) {}
    if (!sets.length) return;

    var set = null;
    var showPrice = root.getAttribute('data-show-price') === 'true';

    /* Match the store's money format for prices we build ourselves, by reading
       a sample the theme rendered (e.g. "Rs. 1,234.00") and reusing its shape. */
    var sample = (root.querySelector('[data-money-sample]') || {}).textContent || 'Rs. 1,234.00';
    /* Everything before the first digit is the prefix. Stripping trailing
       digits/dots instead ate the full stop in "Rs." and prices came out as
       "Rs 20.00". */
    var moneyPrefix = sample.replace(/[\d].*$/, '').trim();
    var moneyDecimals = /[.,]\d{2}$/.test(sample.trim()) ? 2 : 0;
    function money(v) {
      var n = parseFloat(v);
      if (isNaN(n)) return '';
      try { return moneyPrefix + ' ' + n.toLocaleString('en-IN', { minimumFractionDigits: moneyDecimals, maximumFractionDigits: moneyDecimals }); }
      catch (e) { return moneyPrefix + ' ' + n.toFixed(moneyDecimals); }
    }

    /* Pull an entire collection from Shopify's public products.json, paging
       until it runs out. Sold-out products and ones without a photo are dropped
       — nobody ordering 300 pieces wants to be shown something unavailable. */
    function fetchAll(s) {
      if (s.__loading || s.__complete) return Promise.resolve();
      s.__loading = true;
      var out = [];
      function page(n) {
        return fetch('/collections/' + encodeURIComponent(s.handle) + '/products.json?limit=' + PER_PAGE + '&page=' + n)
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (d) {
            var ps = d.products || [];
            ps.forEach(function (pr) {
              if (!pr.images || !pr.images.length) return;
              var v = (pr.variants || [])[0] || {};
              if (v.available === false) return;
              out.push({
                img: String(pr.images[0].src).split('?')[0] + '?crop=center&height=300&width=300',
                title: pr.title,
                price: money(v.price)
              });
            });
            if (ps.length === PER_PAGE && n < 6) return page(n + 1);
          });
      }
      return page(1).then(function () {
        if (out.length) s.items = out;
        s.__complete = true;
        s.__loading = false;
      }).catch(function () { s.__loading = false; });
    }

    /* One tile. Used for the first paint and for the background top-up, so both
       paths produce identical markup. */
    function tile(it, i) {
      /* A BUTTON, NOT A LINK — deliberately. Every product link is a door out
         of a page whose only job is collecting an enquiry. */
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bulkw__t';
      b.title = it.title;
      b.setAttribute('aria-label', 'Add ' + it.title + ' to my enquiry');

      var img = document.createElement('img');
      img.src = it.img;
      img.alt = it.title;
      img.width = 200; img.height = 200;
      img.decoding = 'async';
      img.loading = i < 20 ? 'eager' : 'lazy';
      img.addEventListener('load', function () { this.classList.add('is-loaded'); });
      img.addEventListener('error', function () { this.classList.add('is-loaded'); });
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
      b.appendChild(img);

      if (showPrice) {
        var p = document.createElement('span');
        p.className = 'bulkw__p';
        p.textContent = it.price || '';
        b.appendChild(p);
      }
      return b;
    }

    function render(index) {
      set = sets[index];
      if (!set) return;

      chips.forEach(function (c, i) {
        c.classList.toggle('is-on', i === index);
        c.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      if (nameEl) nameEl.textContent = set.label || '';

      var list = set.items || [];
      var frag = document.createDocumentFragment();

      list.forEach(function (it, i) { frag.appendChild(tile(it, i)); });

      wall.replaceChildren(frag);
      wall.scrollTop = 0;
      root.dispatchEvent(new CustomEvent('crate:change', { bubbles: true, detail: { label: set.label } }));

      /* Then top the range up in the background.
         APPEND the extra tiles — do NOT re-render. Rebuilding the whole wall
         reflowed content the shopper was already looking at and cost real CLS.
         Appending below the fold moves nothing. */
      var target = set;
      var painted = list.length;
      if (!target.__complete) {
        wall.setAttribute('aria-busy', 'true');
        fetchAll(target).then(function () {
          wall.removeAttribute('aria-busy');
          if (set !== target) return;

          var chip = chips[index] && chips[index].querySelector('.bulkp-switch__n');
          if (chip && target.items.length) chip.textContent = target.items.length;

          var extra = (target.items || []).slice(painted);
          if (!extra.length) return;
          var f2 = document.createDocumentFragment();
          extra.forEach(function (it) { f2.appendChild(tile(it, painted + 1)); });
          wall.appendChild(f2);
        });
      }
    }

    /* One delegated listener for the whole wall rather than sixty separate ones. */
    wall.addEventListener('click', function (e) {
      var el = e.target.closest('.bulkw__t');
      if (!el || !set) return;
      el.classList.add('is-added');
      setTimeout(function () { el.classList.remove('is-added'); }, 1600);
      document.dispatchEvent(new CustomEvent('bulk:interest', {
        bubbles: true, detail: { category: set.cat || '', title: el.title }
      }));
      document.dispatchEvent(new CustomEvent('bulk:pick', {
        bubbles: true, detail: { title: el.title }
      }));
      if (navigator.vibrate) { try { navigator.vibrate(8); } catch (err) {} }
      try { if (window.dataLayer) window.dataLayer.push({ event: 'bulk_wall_add', category: set.cat }); } catch (err) {}
    });

    chips.forEach(function (chip, i) {
      chip.addEventListener('click', function () { render(i); });
    });

    render(0);
  }

  function boot() { document.querySelectorAll('[data-bulk-hero]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
